<template>
  <v-bottom-sheet scrollable :value="value" @input="$emit('input', $event)">
    <v-card v-if="value">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Inventory #{{ inventoryId }} - Availability</span>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </v-card-text>

      <v-card-text v-else>
        <!-- Weight Breakdown Header -->
        <v-sheet class="pa-4 mb-4" rounded outlined>
          <v-row dense>
            <v-col cols="12">
              <div class="d-flex justify-space-between text-body-2">
                <span>Original Weight:</span>
                <span>{{ formatWeight(originalWeight) }} kg</span>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="d-flex justify-space-between text-body-2">
                <span>Used Weight:</span>
                <span>-{{ formatWeight(usedWeight) }} kg</span>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="d-flex justify-space-between text-body-2">
                <span>Reserved Weight:</span>
                <span class="orange--text">-{{ formatWeight(reservedWeight) }} kg</span>
              </div>
            </v-col>
            <v-col cols="12">
              <v-divider class="my-1" />
              <div class="d-flex justify-space-between text-body-1 font-weight-bold">
                <span>Freely Available:</span>
                <span :class="freeWeight > 0 ? 'green--text' : 'red--text'">
                  {{ formatWeight(freeWeight) }} kg
                </span>
              </div>
            </v-col>
          </v-row>

          <v-progress-linear
            class="mt-4"
            :value="usedPercent"
            :buffer-value="usedPercent + reservedPercent"
            color="grey"
            buffer-color="orange"
            background-color="green lighten-3"
            height="20"
            rounded
          />
          <div class="d-flex justify-space-between text-caption mt-1">
            <span class="grey--text">Used {{ usedPercent.toFixed(1) }}%</span>
            <span class="orange--text">Reserved {{ reservedPercent.toFixed(1) }}%</span>
            <span class="green--text">Free {{ freePercent.toFixed(1) }}%</span>
          </div>
        </v-sheet>

        <v-divider class="mb-4" />

        <!-- Reservation List -->
        <div class="text-subtitle-1 font-weight-medium mb-2">
          Reservations ({{ reservations.length }})
        </div>

        <v-simple-table v-if="reservations.length > 0" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th>Worksheet ID</th>
                <th>Entry ID</th>
                <th class="text-right">Quantity (kg)</th>
                <th>Reserved On</th>
                <th>Age</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in reservations" :key="r.id">
                <td>
                  <a class="primary--text" @click="openWorksheet(r.processWorksheetId)">
                    {{ r.processWorksheetId }}
                  </a>
                </td>
                <td>{{ r.processWorksheetInputId || '-' }}</td>
                <td class="text-right">{{ r.quantity | formatNumber }}</td>
                <td>{{ r.createTime | formatDateYear }}</td>
                <td>{{ getAge(r.createTime) }}</td>
                <td class="text-right">
                  <v-btn
                    icon
                    small
                    color="orange"
                    title="Release reservation"
                    :loading="releasingId === r.id"
                    @click="confirmRelease(r)"
                  >
                    <v-icon small>mdi-lock-open-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div v-else class="text-center text-caption grey--text pa-4">
          No active reservations for this inventory.
        </div>
      </v-card-text>

      <!-- Release Confirmation Dialog -->
      <v-dialog v-model="confirmDialog" max-width="400" persistent>
        <v-card>
          <v-card-title class="text-subtitle-1">Confirm Release</v-card-title>
          <v-card-text v-if="releaseTarget">
            Release reservation of
            <strong>{{ releaseTarget.quantity | formatNumber }} kg</strong>
            from Worksheet #{{ releaseTarget.processWorksheetId }}?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="confirmDialog = false">Cancel</v-btn>
            <v-btn color="orange" text @click="doRelease">Release</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-bottom-sheet>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import { mapActions } from "vuex";
import {
  releaseInventoryReservation,
  getReservationByInventoryIds,
} from "@/services/inventoryReservation";

export default {
  name: "InventoryAvailabilityView",

  props: {
    inventoryId: {
      type: Number,
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      inventory: null,
      reservations: [],
      confirmDialog: false,
      releaseTarget: null,
      releasingId: null,
    };
  },

  computed: {
    originalWeight() {
      return this.inventory ? this.inventory.weight || 0 : 0;
    },

    usedWeight() {
      if (!this.inventory) return 0;
      const sumQuantity = (arr) =>
        (arr || []).reduce((sum, item) => sum + (item.quantity || 0), 0);
      return (
        sumQuantity(this.inventory.processInputs) +
        sumQuantity(this.inventory.packingItemInventories) +
        sumQuantity(this.inventory.adjustmentDeductions)
      );
    },

    reservedWeight() {
      return this.reservations.reduce(
        (sum, r) => sum + (r.quantity || 0),
        0
      );
    },

    freeWeight() {
      return this.originalWeight - this.usedWeight - this.reservedWeight;
    },

    usedPercent() {
      if (this.originalWeight <= 0) return 0;
      return Math.min((this.usedWeight / this.originalWeight) * 100, 100);
    },

    reservedPercent() {
      if (this.originalWeight <= 0) return 0;
      return Math.min((this.reservedWeight / this.originalWeight) * 100, 100);
    },

    freePercent() {
      const free = 100 - this.usedPercent - this.reservedPercent;
      return Math.max(free, 0);
    },
  },

  methods: {
    ...mapActions("messages", ["addErrorMessage", "addSuccessMessage"]),

    formatWeight(value) {
      if (value == null) return "0";
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getAge(dateStr) {
      if (!dateStr) return "-";
      const days = moment().diff(moment(dateStr), "days");
      if (days === 0) return "Today";
      if (days === 1) return "1 day";
      return `${days} days`;
    },

    openWorksheet(worksheetId) {
      if (worksheetId) {
        this.$router.push({
          name: "processWorksheetView",
          params: { id: worksheetId },
        });
      }
    },

    confirmRelease(reservation) {
      this.releaseTarget = reservation;
      this.confirmDialog = true;
    },

    async doRelease() {
      if (!this.releaseTarget) return;
      const r = this.releaseTarget;
      this.confirmDialog = false;
      this.releasingId = r.id;
      try {
        await releaseInventoryReservation(
          r.processWorksheetId,
          this.inventoryId,
          r.quantity,
          r.inventoryUnitId
        );
        this.addSuccessMessage("Reservation released successfully");
        await this.fetchData();
      } catch {
        this.addErrorMessage("Failed to release reservation");
      } finally {
        this.releasingId = null;
        this.releaseTarget = null;
      }
    },

    async fetchData() {
      if (!this.inventoryId) return;
      this.loading = true;
      try {
        const [inventoryResponse, reservationData] = await Promise.all([
          Vue.axios.get("/inventory/" + this.inventoryId),
          getReservationByInventoryIds([this.inventoryId]),
        ]);
        this.inventory = inventoryResponse.data;
        this.reservations = reservationData || [];
      } catch {
        this.addErrorMessage("Failed to load inventory availability data");
        this.inventory = null;
        this.reservations = [];
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    inventoryId: {
      handler(newVal) {
        if (newVal && this.value) {
          this.fetchData();
        }
      },
    },
    value: {
      handler(newVal) {
        if (newVal && this.inventoryId) {
          this.fetchData();
        }
      },
    },
  },

  mounted() {
    if (this.value && this.inventoryId) {
      this.fetchData();
    }
  },
};
</script>

<style scoped>
a {
  cursor: pointer;
  text-decoration: underline;
}
</style>
